import { motion } from 'framer-motion'
import { 
  Gift, 
  TrendingUp, 
  Share2, 
  Zap,
  Award
} from 'lucide-react'
import { mockRewardStats, mockRewards } from '@/data/mockData'
import { formatCurrency, formatDate } from '@/lib/utils'
import Button from '@/components/ui/Button'
import { toast } from 'sonner'

export default function Rewards() {
  const handleShare = () => {
    toast.info('Share referral link coming soon!')
  }
  
  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Rewards</h1>
        <Button variant="secondary" size="sm" onClick={handleShare}>
          <Share2 className="w-4 h-4 mr-1" />
          Share
        </Button>
      </div>
      
      {/* Points Balance */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand/20 to-accent-cyan/20 border border-brand/30 p-6"
      >
        <div className="absolute -top-20 -right-20 w-40 h-40 bg-brand/30 rounded-full blur-3xl" />
        
        <div className="relative z-10 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Award className="w-5 h-5 text-brand" />
            <span className="text-text-secondary">Total Points</span>
          </div>
          <p className="text-balance text-text-primary">
            {mockRewardStats.totalPoints.toLocaleString()}
          </p>
          <p className="text-text-secondary mt-2">
            ≈ {formatCurrency(mockRewardStats.pointsValue)}
          </p>
        </div>
      </motion.div>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 rounded-2xl bg-background-elevated border border-border">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-accent-green" />
            <span className="text-sm text-text-secondary">Cashback Rate</span>
          </div>
          <p className="text-2xl font-bold text-text-primary">
            {mockRewardStats.cashbackRate}%
          </p>
        </div>
        
        <div className="p-4 rounded-2xl bg-background-elevated border border-border">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-4 h-4 text-accent-orange" />
            <span className="text-sm text-text-secondary">Monthly</span>
          </div>
          <p className="text-2xl font-bold text-text-primary">
            {formatCurrency(mockRewardStats.monthlyEarnings)}
          </p>
        </div>
      </div>
      
      {/* Lifetime Earnings */}
      <div className="p-4 rounded-2xl bg-background-elevated border border-border">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-text-secondary mb-1">Lifetime Earnings</p>
            <p className="text-3xl font-bold text-text-primary">
              {formatCurrency(mockRewardStats.lifetimeEarnings)}
            </p>
          </div>
          <div className="w-14 h-14 rounded-2xl bg-brand/20 flex items-center justify-center">
            <Gift className="w-7 h-7 text-brand" />
          </div>
        </div>
      </div>
      
      {/* Recent Rewards */}
      <section>
        <h2 className="text-lg font-semibold mb-4">Recent Rewards</h2>
        <div className="space-y-3">
          {mockRewards.map((reward, index) => (
            <motion.div
              key={reward.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="flex items-center justify-between p-4 rounded-2xl bg-background-elevated border border-border"
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  reward.type === 'cashback' ? 'bg-green-500/20' :
                  reward.type === 'referral' ? 'bg-blue-500/20' :
                  'bg-purple-500/20'
                }`}>
                  {reward.type === 'cashback' && <TrendingUp className="w-5 h-5 text-accent-green" />}
                  {reward.type === 'referral' && <Share2 className="w-5 h-5 text-blue-500" />}
                  {reward.type === 'staking' && <Zap className="w-5 h-5 text-purple-500" />}
                </div>
                <div>
                  <p className="font-medium capitalize">{reward.type}</p>
                  <p className="text-sm text-text-tertiary">{reward.description}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-semibold text-accent-green">
                  +{formatCurrency(reward.amount)}
                </p>
                <p className="text-xs text-text-tertiary">
                  {reward.points.toLocaleString()} pts
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>
      
      {/* Referral Section */}
      <section className="p-4 rounded-2xl bg-gradient-to-br from-brand/10 to-accent-cyan/10 border border-brand/20">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-brand/20 flex items-center justify-center flex-shrink-0">
            <Share2 className="w-6 h-6 text-brand" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-lg mb-1">Refer Friends</h3>
            <p className="text-text-secondary text-sm mb-4">
              Invite friends to Capybank and earn 1,000 points for each signup!
            </p>
            <Button variant="primary" size="sm" onClick={handleShare} className="w-full">
              Share Referral Link
            </Button>
          </div>
        </div>
      </section>
    </div>
  )
}